import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Button, IconButton, InputAdornment, Stack, TextField, Typography, CircularProgress } from '@mui/material';
import { Send, DeleteOutline, Favorite, FavoriteBorder } from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  ROUTINE_COMMENTS,
  ADD_ROUTINE_COMMENT,
  DELETE_ROUTINE_COMMENT,
  TOGGLE_COMMENT_LIKE,
  SHARED_ROUTINES
} from 'api/graphql/play';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

const initials = (name) => (name || '?').trim().charAt(0).toUpperCase();
const ago = (iso) => {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: es });
  } catch {
    return '';
  }
};

/**
 * OTRIX Play — Instagram-style comment thread. Comments on top, composer at the bottom.
 * Like / reply (one level of threads) / delete own. In `previewCount` mode it shows only the
 * latest N and a "view all" link (used inline in the feed); full mode powers the modal.
 */
function PostComments({
  routineId = null,
  previewCount = 0,
  onSeeAll = null,
  onOpenAuthor = null
}) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const currentUserId = useSelector((s) => s.auth.user?.id);

  const [rootText, setRootText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const refetch = [
    { query: ROUTINE_COMMENTS, variables: { shared_routine_id: routineId } },
    { query: SHARED_ROUTINES, variables: { muscle_focus: undefined } }
  ];

  const { data, loading } = useQuery(ROUTINE_COMMENTS, {
    variables: { shared_routine_id: routineId },
    skip: !routineId,
    fetchPolicy: 'cache-and-network'
  });
  const comments = data?.routineComments || [];

  const [addComment, { loading: adding }] = useMutation(ADD_ROUTINE_COMMENT, { refetchQueries: refetch });
  const [deleteComment] = useMutation(DELETE_ROUTINE_COMMENT, { refetchQueries: refetch });
  const [toggleCommentLike] = useMutation(TOGGLE_COMMENT_LIKE);

  const toast = (message) =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `cm-${Date.now()}`, variant: 'error', message })));

  const submit = async (body, parentId, reset) => {
    if (!body.trim()) return;
    try {
      await addComment({ variables: { shared_routine_id: routineId, parent_id: parentId || null, body: body.trim() } });
      reset();
    } catch {
      toast(t('forum.commentError', { ns: 'play', defaultValue: 'No se pudo enviar el comentario.' }));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteComment({ variables: { id } });
    } catch {
      toast(t('forum.commentError', { ns: 'play', defaultValue: 'No se pudo enviar el comentario.' }));
    }
  };

  const handleLike = async (id) => {
    try {
      await toggleCommentLike({ variables: { id } });
    } catch {
      toast(t('forum.commentError', { ns: 'play', defaultValue: 'No se pudo enviar el comentario.' }));
    }
  };

  const authorName = (c) => c.user?.full_name || c.author_name || t('forum.someone', { ns: 'play', defaultValue: 'Alguien' });

  const renderComment = (c, isReply = false) => (
    <Stack key={c.id} direction="row" spacing={1.25} sx={{ pl: isReply ? 4 : 0 }}>
      <Avatar
        src={c.user?.photo || undefined}
        onClick={onOpenAuthor && c.user?.id ? () => onOpenAuthor(c.user.id) : undefined}
        sx={{ width: 30, height: 30, bgcolor: 'gold.soft', color: 'gold.main', fontSize: '0.8rem', flex: 'none', cursor: onOpenAuthor && c.user?.id ? 'pointer' : 'default' }}>
        {initials(authorName(c))}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'surface.raised', border: '1px solid', borderColor: 'hairline.main' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" fontWeight={800}>
              {authorName(c)}
            </Typography>
            {String(c.user_id) === String(currentUserId) && (
              <IconButton size="small" aria-label={t('forum.deleteComment', { ns: 'play', defaultValue: 'Borrar' })} onClick={() => handleDelete(c.id)} sx={{ color: 'sessionStatus.absent.fg', p: 0.25 }}>
                <DeleteOutline sx={{ fontSize: 15 }} />
              </IconButton>
            )}
          </Stack>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.25 }}>
            {c.body}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {ago(c.created_at)}
          </Typography>
          <Button
            size="small"
            startIcon={c.is_liked ? <Favorite sx={{ fontSize: 14, color: 'sessionStatus.absent.fg' }} /> : <FavoriteBorder sx={{ fontSize: 14 }} />}
            onClick={() => handleLike(c.id)}
            sx={{ color: c.is_liked ? 'sessionStatus.absent.fg' : 'text.secondary', textTransform: 'none', fontWeight: 700, minWidth: 0, p: 0 }}>
            {c.like_count || 0}
          </Button>
          {!isReply && (
            <Button
              size="small"
              onClick={() => {
                setReplyTo(replyTo === c.id ? null : c.id);
                setReplyText('');
              }}
              sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 700, minWidth: 0, p: 0 }}>
              {t('forum.reply', { ns: 'play', defaultValue: 'Responder' })}
            </Button>
          )}
        </Stack>

        {(c.replies || []).map((r) => renderComment(r, true))}

        {replyTo === c.id && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={t('forum.replyPlaceholder', { ns: 'play', defaultValue: 'Escribe una respuesta…' })}
            />
            <IconButton color="secondary" disabled={adding || !replyText.trim()} onClick={() => submit(replyText, c.id, () => { setReplyText(''); setReplyTo(null); })}>
              <Send />
            </IconButton>
          </Stack>
        )}
      </Box>
    </Stack>
  );

  const total = comments.length;
  const shown = previewCount > 0 ? comments.slice(0, previewCount) : comments;

  return (
    <Box>
      {previewCount > 0 && total > previewCount && (
        <Button size="small" onClick={onSeeAll} sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 700, px: 0, mb: 1 }}>
          {t('forum.viewAll', { ns: 'play', count: total, defaultValue: `Ver los ${total} comentarios` })}
        </Button>
      )}

      {loading && total === 0 ? (
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={20} color="secondary" />
        </Stack>
      ) : (
        <Stack spacing={1.5} sx={{ mb: shown.length ? 1.5 : 0 }}>
          {shown.map((c) => renderComment(c))}
        </Stack>
      )}

      {/* Composer: pastilla con el enviar DENTRO del campo (lenguaje de Facebook/IG). El Send
          suelto al lado partía la fila en dos y a 320 dejaba el campo sin ancho. */}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={4}
          value={rootText}
          onChange={(e) => setRootText(e.target.value)}
          placeholder={t('forum.commentPlaceholder', { ns: 'play', defaultValue: 'Escribe un comentario…' })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  color="secondary"
                  aria-label={t('forum.send', { ns: 'play', defaultValue: 'Enviar' })}
                  disabled={adding || !rootText.trim()}
                  onClick={() => submit(rootText, null, () => setRootText(''))}>
                  <Send fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: { borderRadius: 999, bgcolor: 'surface.raised', pr: 0.5 }
          }}
        />
      </Stack>
    </Box>
  );
}

PostComments.propTypes = {
  routineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  previewCount: PropTypes.number,
  onSeeAll: PropTypes.func,
  onOpenAuthor: PropTypes.func
};

export default PostComments;
